import { inject, injectable } from "inversify";
import * as THREE from "three";
import { EngineConfigService, type PointerPosition } from "@designboticteam/core";
import {
	CAMERA_CONTROL_SERVICE,
	type Circle,
	type CircleHandle,
	CircleTool,
	CursorSnappingService,
	type ICameraControlService,
	SceneService,
	type Shape,
	ShapeEditingService,
	VERTEX_PICK_PX,
	Workspace3DStoreService,
} from "@designboticteam/workspace-3d";
import { Kolega3DStoreService } from "../../services/Kolega3DStoreService";
import { ProjectArea } from "./ProjectArea";

const MAX_RADIUS = 500;

@injectable()
class ProjectAreaTool extends CircleTool {
	public static readonly toolName: string = "ProjectAreaTool";
	public override readonly toolName: string = ProjectAreaTool.toolName;

	private projectArea: ProjectArea | null = null;
	public getProjectArea(): ProjectArea | null {
		// TODO: implement serialization here?
		return this.projectArea;
	}

	private isEditing = false;
	private handleAtPointerDown: CircleHandle | null = null;
	private draggedHandle: CircleHandle | null = null;

	constructor(
		@inject(SceneService) sceneService: SceneService,
		@inject(CursorSnappingService) cursorSnappingService: CursorSnappingService,
		@inject(ShapeEditingService) shapeEditingService: ShapeEditingService,
		@inject(Workspace3DStoreService) workspace3DStoreService: Workspace3DStoreService,
		@inject(Kolega3DStoreService) private readonly kolega3DStoreService: Kolega3DStoreService,
		@inject(EngineConfigService) private readonly engineConfigService: EngineConfigService,
		@inject(CAMERA_CONTROL_SERVICE) private readonly cameraControlService: ICameraControlService,
	) {
		super(sceneService, cursorSnappingService, workspace3DStoreService, shapeEditingService);
	}

	/** */
	public startEditing(): void {
		if (this.projectArea === null) {
			return;
		}

		this.isEditing = true;
	}

	public override onDeactivate(): void {
		super.onDeactivate();
		this.endDrag();
		this.projectArea?.setHighlightedHandle(null);
		this.isEditing = false;
	}

	public override onTap(position: PointerPosition): void {
		if (this.isEditing) {
			return;
		}

		super.onTap(position);
	}

	public override onPointerMove(): void {
		if (this.isEditing === false) {
			super.onPointerMove();
			return;
		}

		if (this.draggedHandle === null) {
			this.projectArea?.setHighlightedHandle(this.findHandleUnderCursor());
		}
	}

	public onPointerDown(): void {
		if (this.isEditing) {
			this.handleAtPointerDown = this.findHandleUnderCursor();
		}
	}

	public onDragStart(): void {
		if (this.isEditing === false) {
			return;
		}

		this.draggedHandle = this.handleAtPointerDown;

		if (this.draggedHandle !== null) {
			this.cameraControlService.disable();
		}
	}

	public onDragMove(): void {
		if (this.draggedHandle === null || this.projectArea === null) {
			return;
		}

		const { worldPosition } = this.cursorSnappingService.pickPosition();

		if (worldPosition === null) {
			return;
		}

		if (this.draggedHandle === "center") {
			this.projectArea.setCenter(worldPosition);
			return;
		}

		this.updateShapeRadius({ shape: this.projectArea, radiusTerminalPoint: worldPosition });
	}

	public onDragEnd(): void {
		if (this.draggedHandle !== null) {
			this.kolega3DStoreService.store.setState({ isValidationDirty: true });
		}

		this.endDrag();
	}

	private endDrag(): void {
		if (this.draggedHandle !== null) {
			this.cameraControlService.enable();
		}

		this.draggedHandle = null;
		this.handleAtPointerDown = null;
	}

	private findHandleUnderCursor(): CircleHandle | null {
		const center = this.projectArea?.getCenter() ?? null;
		const radiusEnd = this.projectArea?.getRadiusEnd() ?? null;
		const { screenPosition } = this.cursorSnappingService.pickPosition();

		if (center === null || radiusEnd === null || screenPosition === null) {
			return null;
		}

		let nearestHandle: CircleHandle | null = null;
		let lowestDistanceSq = VERTEX_PICK_PX * VERTEX_PICK_PX;

		for (const [handle, point] of [
			["center", center],
			["radius", radiusEnd],
		] as const) {
			const pointOnScreen = this.cursorSnappingService.getPositionOnViewport(point);

			if (pointOnScreen === null) {
				continue;
			}

			const dx = screenPosition.x - pointOnScreen.x;
			const dy = screenPosition.y - pointOnScreen.y;
			const distanceSq = dx * dx + dy * dy;

			if (distanceSq < lowestDistanceSq) {
				lowestDistanceSq = distanceSq;
				nearestHandle = handle;
			}
		}

		return nearestHandle;
	}

	/** Builds the area programmatically (auto mode) through the same accept path as drawing it. */
	public setProjectArea({ center, radius }: { center: THREE.Vector3; radius: number }): void {
		this.discardCurrentShape();

		const shape = this.createShape();
		shape.start({ initialPoint: center });
		shape.updateRadius({ value: radius, direction: new THREE.Vector3(1, 0, 0) });
		this.shapes.push(shape);
		this.currentShape = shape;
		this.applyLabelsVisibility();
		this.sceneService.scene.add(shape.sceneObject);

		this.acceptCurrentShape();
	}

	/** */
	public clearAll(): void {
		if (this.projectArea === null) {
			return;
		}

		this.projectArea.dispose();
		this.projectArea = null;
		this.kolega3DStoreService.store.setState({ projectArea: null, isValidationDirty: true });
	}

	/** */
	public deleteShape(shape: Shape): boolean {
		if (this.projectArea === null || shape !== this.projectArea) {
			return false;
		}

		this.projectArea.dispose();
		this.projectArea = null;
		this.kolega3DStoreService.store.setState({ projectArea: null, isValidationDirty: true });
		return true;
	}

	/** */
	protected override createShape(): Circle {
		const palette = this.engineConfigService.palette;

		return new ProjectArea({
			maxRadius: MAX_RADIUS,
			circleColor: palette.main.secondary,
			centerMarkerColor: palette.main.secondary,
			radiusLineColor: palette.main.secondary,
		});
	}

	/** */
	protected override onShapeAccepted(shape: Circle): void {
		if (this.projectArea !== null && this.projectArea !== shape) {
			this.shapeEditingService.unregisterShape(this.projectArea);
			this.projectArea.dispose();
		}

		if (shape instanceof ProjectArea) {
			this.projectArea = shape;
			this.kolega3DStoreService.store.setState({ projectArea: this.projectArea, isValidationDirty: true });
		}
	}
}

export { ProjectAreaTool };
